import React, { useState, useEffect } from "react";
import { Container, Form, Button, Table, Alert, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { 
  selectCustomRoles,
  addCustomRole,
  updateCustomRole,
  deleteCustomRole
} from "../../redux/slices/customRolesSlice";
import { persistor } from "../../redux/store"; // Import persistor to flush changes

// Create the component
function AdminManageRoles() {
  const dispatch = useDispatch();
  const customRoles = useSelector(selectCustomRoles);
  
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !role) {
      setStatusMessage({ type: "danger", message: "Email and Role are required" });
      return;
    }

    if (editMode) {
      // Update existing role
      dispatch(updateCustomRole({ email, role }));
      setStatusMessage({ type: "success", message: `Updated role for ${email}` });
    } else {
      // Add new role
      if (customRoles[email]) {
        setStatusMessage({ type: "warning", message: "This email already has a custom role. Edit it instead." });
        return;
      }
      dispatch(addCustomRole({ email, role }));
      setStatusMessage({ type: "success", message: `Added ${role} role for ${email}` });
    }

    // Force persistence to localStorage after state change
    persistor.flush();

    // Reset form
    setEmail("");
    setRole("");
    setEditMode(false);
  };

  const handleEdit = (email, role) => {
    setEmail(email);
    setRole(role);
    setEditMode(true);
  };

  const handleDelete = (email) => {
    dispatch(deleteCustomRole(email));
    // Force persistence to localStorage after deletion
    persistor.flush();
    setStatusMessage({ type: "success", message: `Removed custom role for ${email}` });
  };

  const handleCancel = () => {
    setEmail("");
    setRole("");
    setEditMode(false);
    setStatusMessage({ type: "", message: "" });
  };
  
  // Debug - log customRoles whenever they change
  useEffect(() => {
    console.log("Custom roles updated:", customRoles);
  }, [customRoles]);

  return (
    <Container>
      <h3>Manage Custom User Roles</h3>
      <p>
        This page allows you to assign custom roles to users that are not in the standard user groups. 
        These roles will be used in addition to any roles assigned through Azure AD groups.
      </p>

      {statusMessage.message && (
        <Alert variant={statusMessage.type} onClose={() => setStatusMessage({ type: "", message: "" })} dismissible>
          {statusMessage.message}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={5}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                required
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select value={role} onChange={(e) => setRole(e.target.value)} required>
                <option value="">Select a role...</option>
                <option value="Admin">Admin</option>
                <option value="Scheduling">Scheduling</option>
                <option value="EngineerManager">EngineerManager</option>
                <option value="Accounting">Accounting</option>
                <option value="MechanicalEngineer">MechanicalEngineer</option>
                <option value="ElectricalEngineer">ElectricalEngineer</option>
                <option value="ControlEngineer">ControlEngineer</option>
                <option value="Apps">Apps</option>
                <option value="AppsManager">AppsManager</option>
                <option value="PSN">PSN</option>
                <option value="Sales">Sales</option>
                <option value="ReadOnly">ReadOnly</option>
                <option value="NorthPrairie">NorthPrairie</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button type="submit" variant="primary" className="mb-3 me-2">
              {editMode ? "Update Role" : "Add Role"}
            </Button>
            {editMode && (
              <Button type="button" variant="secondary" className="mb-3" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <Table striped bordered hover className="mt-4">
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(customRoles).map(([email, role]) => (
            <tr key={email}>
              <td>{email}</td>
              <td>{role}</td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={() => handleEdit(email, role)}
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(email)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
          {Object.keys(customRoles).length === 0 && (
            <tr>
              <td colSpan="3" className="text-center">
                No custom roles defined
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
}

export default AdminManageRoles;